// src/TranslationStatusContext.tsx
// NOTE: Behavioral helpers are verified via frontend/src/__tests__/TranslationStatusContext.test.ts.
// Run `../scripts/test-frontend.sh TranslationStatusContext` when updating logic here.
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import axios from 'axios';
import { debounce } from 'lodash';
import { getAuth } from 'firebase/auth';
import {
  collection,
  onSnapshot,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { TranslationStatus } from './types';
import { db } from './firebaseConfig';
import { useTutorialSandbox } from './tutorials/TutorialSandboxProvider';

const API_BASE = resolveFrontendApiBase();

function buildRealtimeAllowlist(): Set<string> | null {
  const raw = (process.env.REACT_APP_REALTIME_TRANSLATION_USER_IDS || '').trim();
  if (!raw || raw === '*') {
    return null;
  }
  const entries = raw
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean);
  if (entries.length === 0) {
    return null;
  }
  return new Set(entries);
}

const REALTIME_TRANSLATION_ALLOWLIST: Set<string> | null = buildRealtimeAllowlist();
const GLOSSARY_SCAN_TYPE_KEYS = new Set(['glossary scan', 'ai glossary scan', 'term scan', 'terms scan']);
const GLOSSARY_SCAN_ESTIMATE_MINUTES = 10;
const TERMINAL_STATUS_ALIASES: Record<string, 'completed' | 'failed'> = {
  completed: 'completed', succeeded: 'completed', finished: 'completed',
  failed: 'failed', error: 'failed', expired: 'failed', interrupted: 'failed',
  released: 'failed', cancelled: 'failed', canceled: 'failed', aborted: 'failed',
};

function realtimeUpdatesEnabledForUser(userId?: string | null): boolean {
  if (!userId) {
    return false;
  }
  if (REALTIME_TRANSLATION_ALLOWLIST === null) {
    return true;
  }
  if (REALTIME_TRANSLATION_ALLOWLIST.size === 0) {
    return false;
  }
  return REALTIME_TRANSLATION_ALLOWLIST.has(userId);
}

interface TranslationStatusContextType {
  translations: TranslationStatus[];
  clientIntents: TranslationStatus[];
  refreshTranslations: (force?: boolean) => void;
  refreshTranslationsImmediate: (force?: boolean, recover?: boolean) => Promise<void>;
  addOptimisticTranslation: (t: TranslationStatus) => void;
  removeOptimisticTranslation: (lockGuid: string) => void;
  addClientIntent: (t: TranslationStatus) => void;
  clearClientIntent: (lockGuid: string) => void;
  delayNextRefresh: (delayMs?: number) => void;
  updateOptimisticLockGuid: (tempGuid: string, lockGuid: string) => void;
  mergeTranslation: (lockGuid: string, updates: Partial<TranslationStatus>) => void;
  sortConfig: SortConfigByTab;
  updateSortConfig: (tabKey: SortTabKey, updates: Partial<SortConfig>) => void;
  realtimeConnected: boolean;
  realtimeError: string | null;
}

type SortDirection = 'asc' | 'desc';
type SortField = 'time' | 'chapterName' | 'type' | 'language';
type SortFieldSecondary = SortField | 'none';
type SortTabKey = 'pending' | 'completed' | 'failed';
type SortConfig = {
  primaryField: SortField;
  secondaryField: SortFieldSecondary;
  primaryDirection: SortDirection;
  secondaryDirection: SortDirection;
};
type SortConfigByTab = {
  pending: SortConfig;
  completed: SortConfig;
  failed: SortConfig;
};

const SORT_STORAGE_KEY = 'translationStatusSort';
const CLIENT_INTENT_STORAGE_KEY = 'translationClientIntents';
const CLIENT_INTENT_TTL_MS = 30 * 60 * 1000;
const REST_FALLBACK_INITIAL_DELAY_MS = 10_000;
const REST_FALLBACK_INTERVAL_MS = 120_000;
const DEFAULT_SORT_CONFIG: SortConfigByTab = {
  pending: {
    primaryField: 'time',
    secondaryField: 'none',
    primaryDirection: 'desc',
    secondaryDirection: 'asc',
  },
  completed: {
    primaryField: 'time',
    secondaryField: 'none',
    primaryDirection: 'desc',
    secondaryDirection: 'asc',
  },
  failed: {
    primaryField: 'time',
    secondaryField: 'none',
    primaryDirection: 'desc',
    secondaryDirection: 'asc',
  },
};

const SORT_FIELDS: SortField[] = ['time', 'chapterName', 'type', 'language'];
const SORT_FIELDS_SECONDARY: SortFieldSecondary[] = ['none', ...SORT_FIELDS];
const SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

const isValidSortField = (field: unknown): field is SortField =>
  typeof field === 'string' && SORT_FIELDS.includes(field as SortField);

const isValidSortFieldSecondary = (field: unknown): field is SortFieldSecondary =>
  typeof field === 'string' && SORT_FIELDS_SECONDARY.includes(field as SortFieldSecondary);

const isValidSortDirection = (dir: unknown): dir is SortDirection =>
  typeof dir === 'string' && SORT_DIRECTIONS.includes(dir as SortDirection);

const buildSortConfig = (raw: Partial<SortConfigByTab> | null): SortConfigByTab => {
  const fallback = DEFAULT_SORT_CONFIG;
  if (!raw) return fallback;

  const normalize = (input: Partial<SortConfig> | undefined, defaults: SortConfig): SortConfig => {
    if (!input) return defaults;
    return {
      primaryField: isValidSortField(input.primaryField) ? input.primaryField : defaults.primaryField,
      secondaryField: isValidSortFieldSecondary(input.secondaryField) ? input.secondaryField : defaults.secondaryField,
      primaryDirection: isValidSortDirection(input.primaryDirection)
        ? input.primaryDirection
        : defaults.primaryDirection,
      secondaryDirection: isValidSortDirection(input.secondaryDirection)
        ? input.secondaryDirection
        : defaults.secondaryDirection,
    };
  };

  return {
    pending: normalize(raw.pending, fallback.pending),
    completed: normalize(raw.completed, fallback.completed),
    failed: normalize(raw.failed, fallback.failed),
  };
};

const loadSortConfig = (): SortConfigByTab => {
  if (typeof window === 'undefined') return DEFAULT_SORT_CONFIG;
  try {
    const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (!stored) return DEFAULT_SORT_CONFIG;
    const parsed = JSON.parse(stored) as Partial<SortConfigByTab>;
    return buildSortConfig(parsed);
  } catch {
    return DEFAULT_SORT_CONFIG;
  }
};

const TranslationStatusContext = createContext<TranslationStatusContextType | undefined>(undefined);

const toIsoString = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return new Date(value).toISOString();
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return undefined;
};

const normalizeJobType = (value: unknown): string => (value || '').toString().trim().toLowerCase();

const resolveEstimatedMinutes = (
  value: unknown,
  typeKey: string,
): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  if (GLOSSARY_SCAN_TYPE_KEYS.has(typeKey)) {
    return GLOSSARY_SCAN_ESTIMATE_MINUTES;
  }
  return undefined;
};

const loadClientIntents = (): TranslationStatus[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = window.localStorage.getItem(CLIENT_INTENT_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as TranslationStatus[];
    const now = Date.now();
    return (Array.isArray(parsed) ? parsed : [])
      .filter(item => item && item.clientIntent)
      .filter(item => {
        const createdAt = new Date(item.timestamp).getTime();
        return !isNaN(createdAt) && now - createdAt < CLIENT_INTENT_TTL_MS;
      });
  } catch {
    return [];
  }
};

const normalizeMatchToken = (value: unknown): string =>
  String(value || '').trim().toLowerCase();

const matchesTranslation = (intent: TranslationStatus, translation: TranslationStatus): boolean => {
  const intentLockGuid = normalizeMatchToken(intent.lockGuid);
  const intentLockKey = normalizeMatchToken(intent.lockKey);
  const translationLockGuid = normalizeMatchToken(translation.lockGuid);
  const translationLockKey = normalizeMatchToken(translation.lockKey);
  const intentBookId = normalizeMatchToken(intent.bookId);
  const translationBookId = normalizeMatchToken(translation.bookId);
  if (
    intentLockGuid &&
    (intentLockGuid === translationLockGuid || intentLockGuid === translationLockKey) &&
    (!intentBookId || !translationBookId || intentBookId === translationBookId)
  ) {
    return true;
  }
  if (
    intentLockKey &&
    (intentLockKey === translationLockGuid || intentLockKey === translationLockKey) &&
    (!intentBookId || !translationBookId || intentBookId === translationBookId)
  ) {
    return true;
  }

  return Boolean(
    intentBookId &&
    translationBookId &&
    intentBookId === translationBookId &&
    normalizeMatchToken(intent.chapterGuid) === normalizeMatchToken(translation.chapterGuid) &&
    normalizeMatchToken(intent.sourceLang) === normalizeMatchToken(translation.sourceLang) &&
    normalizeMatchToken(intent.targetLang) === normalizeMatchToken(translation.targetLang) &&
    normalizeMatchToken(intent.type) === normalizeMatchToken(translation.type)
  );
};

export const TranslationStatusProvider: React.FC<{ bookId: string; children: ReactNode }> = ({
  bookId,
  children,
}) => {
  const tutorialSandbox = useTutorialSandbox();
  const isTutorialFixtureMode = tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture';
  const tutorialFixturePayload = tutorialSandbox.fixturePayload;
  const tutorialFixtureFingerprint = tutorialFixturePayload?.fingerprint || tutorialSandbox.fixtureId;
  const tutorialFixtureTranslations = useMemo(() => {
    if (!isTutorialFixtureMode || !tutorialFixturePayload) {
      return [];
    }
    const { translationStatuses } = tutorialFixturePayload;
    return Array.isArray(translationStatuses)
      ? translationStatuses as TranslationStatus[]
      : [];
  }, [isTutorialFixtureMode, tutorialFixtureFingerprint, tutorialFixturePayload]);
  const currentUserId = getAuth().currentUser?.uid || '';
  const [translations, setTranslations] = useState<TranslationStatus[]>([]);
  const [clientIntents, setClientIntents] = useState<TranslationStatus[]>(() => loadClientIntents());
  const [realtimeConnected, setRealtimeConnected] = useState<boolean>(false);
  const [realtimeError, setRealtimeError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfigByTab>(() => loadSortConfig());
  const lastFetch = useRef<number>(0);
  const nextAllowedFetch = useRef<number>(0);
  const realtimeConnectedRef = useRef<boolean>(false);
  const realtimeErrorRef = useRef<string | null>(null);
  const MIN_FETCH_INTERVAL = 10_000;
  const OPTIMISTIC_REFRESH_DELAY_MS = 15_000;
  const realtimeUnsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    realtimeConnectedRef.current = realtimeConnected;
  }, [realtimeConnected]);

  useEffect(() => {
    realtimeErrorRef.current = realtimeError;
  }, [realtimeError]);

  useEffect(() => {
    if (!isTutorialFixtureMode) {
      return;
    }
    setTranslations(tutorialFixtureTranslations);
    setRealtimeConnected(false);
    setRealtimeError(null);
    if (realtimeUnsubscribeRef.current) {
      realtimeUnsubscribeRef.current();
      realtimeUnsubscribeRef.current = null;
    }
  }, [isTutorialFixtureMode, tutorialFixtureTranslations]);

  const fetchTranslations = useCallback(
    async (force = false, recover = false) => {
      if (isTutorialFixtureMode) {
        setTranslations(tutorialFixtureTranslations);
        return;
      }
      if (!currentUserId) return;
      const now = Date.now();

      if (!recover && realtimeConnectedRef.current && !realtimeErrorRef.current) {
        return;
      }

      if (!force) {
        if (now < nextAllowedFetch.current) {
          return;
        }
        if (now - lastFetch.current <= MIN_FETCH_INTERVAL) {
          return;
        }
      }

      try {
        const params = new URLSearchParams({ bookId });
        if (recover) {
          params.set('recover', 'true');
        }
        const url = `${API_BASE}/active-translations/${currentUserId}?${params.toString()}`;
        const res = await axios.get<{ translations: TranslationStatus[] }>(url);
        const incoming = res.data.translations || [];
        const hydrated = incoming.map(entry => {
          const typeKey = normalizeJobType(entry.type);
          const estimate = resolveEstimatedMinutes(entry.estimatedTimeMinutes, typeKey);
          if (estimate === undefined) {
            return entry;
          }
          return { ...entry, estimatedTimeMinutes: estimate };
        });
        setTranslations(hydrated);
        lastFetch.current = now;
      } catch (e) {
        console.error('fetchTranslations:', e);
      }
    },
    [bookId, currentUserId, isTutorialFixtureMode, tutorialFixtureTranslations],
  );

  const refreshTranslations = useMemo(
    () => debounce(fetchTranslations, 2_000, { leading: true, trailing: true }),
    [fetchTranslations],
  );

  const refreshTranslationsImmediate = useCallback(async (force = true, recover = false) => {
    await fetchTranslations(force, recover);
  }, [fetchTranslations]);

  const delayNextRefresh = useCallback((delayMs = OPTIMISTIC_REFRESH_DELAY_MS) => {
    const holdUntil = Date.now() + delayMs;
    nextAllowedFetch.current = Math.max(nextAllowedFetch.current, holdUntil);
  }, []);

  const addOptimisticTranslation = useCallback((t: TranslationStatus) => {
    setTranslations(prev => [t, ...prev]);
    delayNextRefresh();
  }, [delayNextRefresh]);

  const removeOptimisticTranslation = useCallback((lockGuid: string) => {
    const normalizedLockGuid = String(lockGuid || '').trim();
    if (!normalizedLockGuid) {
      return;
    }
    setTranslations(prev =>
      prev.filter(entry => {
        const entryLockGuid = String(entry.lockGuid || '').trim();
        const entryLockKey = String(entry.lockKey || '').trim();
        return entryLockGuid !== normalizedLockGuid && entryLockKey !== normalizedLockGuid;
      })
    );
  }, []);

  const addClientIntent = useCallback((t: TranslationStatus) => {
    const createdAt = t.timestamp || new Date().toISOString();
    const intent: TranslationStatus = {
      ...t,
      timestamp: createdAt,
      status: 'pending',
      processingState: t.processingState || 'pending',
      clientIntent: true,
      lockGuid: t.lockGuid || `client-intent-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    setClientIntents(prev => {
      const filtered = prev.filter(existing => !matchesTranslation(intent, existing));
      return [intent, ...filtered];
    });
  }, []);

  const clearClientIntent = useCallback((lockGuid: string) => {
    setClientIntents(prev => prev.filter(intent => intent.lockGuid !== lockGuid));
  }, []);

  const updateOptimisticLockGuid = useCallback((tempGuid: string, lockGuid: string) => {
    if (!tempGuid || !lockGuid || tempGuid === lockGuid) return;
    setTranslations(prev =>
      prev.map(entry =>
        entry.lockGuid === tempGuid ? { ...entry, lockGuid } : entry
      )
    );
  }, []);

  const mergeTranslation = useCallback((lockGuid: string, updates: Partial<TranslationStatus>) => {
    if (!lockGuid || !updates || Object.keys(updates).length === 0) {
      return;
    }
    setTranslations(prev =>
      prev.map(entry =>
        entry.lockGuid === lockGuid ? { ...entry, ...updates } : entry
      )
    );
  }, []);

  const updateSortConfig = useCallback((tabKey: SortTabKey, updates: Partial<SortConfig>) => {
    setSortConfig(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        ...updates,
      },
    }));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sortConfig));
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }, [sortConfig]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(CLIENT_INTENT_STORAGE_KEY, JSON.stringify(clientIntents));
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }, [clientIntents]);

  useEffect(() => {
    if (clientIntents.length === 0) return;
    const now = Date.now();
    setClientIntents(prev =>
      prev.filter(intent => {
        const createdAt = new Date(intent.timestamp).getTime();
        if (isNaN(createdAt) || now - createdAt >= CLIENT_INTENT_TTL_MS) {
          return false;
        }
        return !translations.some(translation => matchesTranslation(intent, translation));
      })
    );
  }, [translations, clientIntents.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setClientIntents(prev =>
        prev.filter(intent => {
          const createdAt = new Date(intent.timestamp).getTime();
          return !isNaN(createdAt) && now - createdAt < CLIENT_INTENT_TTL_MS;
        })
      );
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isTutorialFixtureMode) {
      setRealtimeConnected(false);
      setRealtimeError(null);
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      }
      return;
    }
    if (!bookId || !currentUserId) {
      setRealtimeConnected(false);
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      }
      return;
    }

    const userId = currentUserId;
    if (!realtimeUpdatesEnabledForUser(userId)) {
      setRealtimeConnected(false);
      setRealtimeError(null);
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      }
      return;
    }

    const jobsQuery = query(
      collection(db, 'userActiveJobs', userId, 'jobs'),
      where('bookId', '==', bookId),
    );

    const unsubscribe = onSnapshot(
      jobsQuery,
      snapshot => {
        setRealtimeConnected(true);
        setRealtimeError(null);
        const allowedStatuses: TranslationStatus['status'][] = ['pending', 'queued', 'running', 'retrying', 'completed', 'failed'];
        const allowedStates: TranslationStatus['processingState'][] = ['initializing', 'queued', 'running', 'retrying', 'completed', 'failed', 'pending'];
        const mapped: TranslationStatus[] = snapshot.docs.map(doc => {
          const data = doc.data() || {};
          const completionIso = toIsoString(data.completionTime) || toIsoString(data.failureTime);
          const timestampIso = toIsoString(data.timestamp) || toIsoString(data.updatedAt) || new Date().toISOString();
          const sourceStatus = (data.status || 'pending').toString().trim().toLowerCase();
          const rawStatus = TERMINAL_STATUS_ALIASES[sourceStatus] || sourceStatus;
          const status = (allowedStatuses.includes(rawStatus as TranslationStatus['status'])
            ? rawStatus
            : 'pending') as TranslationStatus['status'];
          const sourceState = (data.processingState || rawStatus || 'pending').toString().trim().toLowerCase();
          // A terminal lock must not become active because its stage projection
          // is stale or because the UI uses a smaller status vocabulary.
          const rawState = TERMINAL_STATUS_ALIASES[sourceStatus]
            || TERMINAL_STATUS_ALIASES[sourceState] || sourceState;
          const processingState = (allowedStates.includes(rawState as TranslationStatus['processingState'])
            ? rawState
            : 'pending') as TranslationStatus['processingState'];
          const typeKey = normalizeJobType(data.type || 'Translation');
          const estimate = resolveEstimatedMinutes(data.estimatedTimeMinutes, typeKey);
          return {
            lockGuid: data.lockGuid || doc.id,
            chapterGuid: data.chapterGuid || '',
            chapterName: data.chapterName || '',
            sourceLang: data.sourceLang || '',
            targetLang: data.targetLang || '',
            timestamp: timestampIso,
            status,
            processingState,
            cleared: Boolean(data.cleared),
            bookId: data.bookId,
            userId,
            word_count: data.word_count,
            estimatedTimeMinutes: estimate,
            completionTime: completionIso,
            lockKey: data.lockKey || doc.id,
            error: data.error,
            errorCode: data.errorCode,
            errorDetails: data.errorDetails,
            errorKind: data.errorKind,
            neverTranslateIssues: data.neverTranslateIssues,
            creditChargeStatus: data.creditChargeStatus,
            supportCode: data.supportCode,
            type: (data.type || 'Translation') as TranslationStatus['type'],
            batchJobId: data.batchJobId,
            batchProvider: data.batchProvider,
            version: data.version,
            format: data.format,
            downloadUrl: data.downloadUrl,
            downloadFilename: data.downloadFilename,
            statusMessage: data.statusMessage,
            blockedByCharacterAnalysis: Boolean(data.blockedByCharacterAnalysis),
            characterAnalysisStatus: data.characterAnalysisStatus,
            characterAnalysisError: data.characterAnalysisError,
            characterAnalysisLockKey: data.characterAnalysisLockKey,
            characterAnalysisManifestId: data.characterAnalysisManifestId,
          };
        });
        setTranslations(mapped);
        lastFetch.current = Date.now();
      },
      error => {
        console.error('Realtime translation listener error', error);
        setRealtimeConnected(false);
        setRealtimeError(error?.message || 'Unable to stream translation updates.');
      },
    );

    realtimeUnsubscribeRef.current = unsubscribe;
    return () => {
      setRealtimeConnected(false);
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      } else {
        unsubscribe();
      }
    };
  }, [bookId, currentUserId, isTutorialFixtureMode]);

  useEffect(() => {
    if (isTutorialFixtureMode) {
      return;
    }
    if (!bookId || !currentUserId) {
      return;
    }

    const realtimeEnabled = realtimeUpdatesEnabledForUser(currentUserId);
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let fallbackInterval: ReturnType<typeof setInterval> | undefined;

    if (realtimeEnabled) {
      fallbackTimer = setTimeout(() => {
        if (!realtimeConnectedRef.current) {
          fetchTranslations(true, true);
        }
      }, REST_FALLBACK_INITIAL_DELAY_MS);

      if (realtimeError) {
        fallbackInterval = setInterval(() => {
          fetchTranslations(false, true);
        }, REST_FALLBACK_INTERVAL_MS);
      }
    } else {
      fetchTranslations(true, true);
      fallbackInterval = setInterval(() => {
        fetchTranslations(false, true);
      }, REST_FALLBACK_INTERVAL_MS);
    }

    return () => {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
      }
    };
  }, [bookId, currentUserId, fetchTranslations, isTutorialFixtureMode, realtimeError]);

  return (
    <TranslationStatusContext.Provider
      value={{
        translations,
        clientIntents,
        refreshTranslations,
        refreshTranslationsImmediate,
        addOptimisticTranslation,
        removeOptimisticTranslation,
        addClientIntent,
        clearClientIntent,
        delayNextRefresh,
        updateOptimisticLockGuid,
        mergeTranslation,
        sortConfig,
        updateSortConfig,
        realtimeConnected,
        realtimeError,
      }}
    >
      {children}
    </TranslationStatusContext.Provider>
  );
};

export function useTranslationStatus(): TranslationStatusContextType {
  const ctx = useContext(TranslationStatusContext);
  if (!ctx) throw new Error('useTranslationStatus must be inside TranslationStatusProvider');
  return ctx;
}
